import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient, type Client } from 'graphql-ws';

/** Backoff between reconnects: 1s doubling to a 30s ceiling — same as SSELink. */
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

export interface WsLinkOptions {
	/** The HTTP GraphQL endpoint; the socket uses the same path over ws(s). */
	uri: string;
	/** Read at connect time, so a reconnect after a token refresh uses the new one. */
	connectionParams: () => Record<string, string>;
}

/**
 * Subscriptions over ONE WebSocket per tab (graphql-ws, `graphql-transport-ws`) —
 * the default transport. `SSELink`, which opens a connection per subscription,
 * remains as the fallback.
 * Browsers allow about six connections per host over HTTP/1.1 — shared by every
 * tab — so a few live features across two apps exhausted them locally and
 * ordinary requests queued behind the streams. One socket carries every
 * subscription in the tab as messages.
 *
 * A socket can't carry request headers, so what `SSELink` sends as headers
 * (`authorization`, `x-app-audience`) goes as connection params instead. The
 * gateway must read the token from there and forward it to the subgraphs — see
 * the note beside `subscriptionTransport` in GraphQLProvider.
 *
 * Same contract as SSELink otherwise: drops reconnect with backoff and
 * resubscribe; a server-side `complete` (the viewer lost access) ends that one
 * subscription for good; nothing missed while disconnected is replayed.
 */
export function createWsLink({ uri, connectionParams }: WsLinkOptions): {
	link: GraphQLWsLink;
	client: Client;
} {
	const url = uri.replace(/^http/, 'ws');
	const client = createClient({
		url,
		connectionParams,
		// Open the socket on the first subscription, close it after the last.
		lazy: true,
		// Keep retrying: a restart or a sleeping laptop is not a reason to go
		// silent until the page is reloaded.
		retryAttempts: Infinity,
		shouldRetry: () => true,
		retryWait: (retries) =>
			new Promise((resolve) =>
				setTimeout(resolve, Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** retries))
			),
		// Pings keep idle proxies from cutting a quiet socket.
		keepAlive: 20_000,
	});
	return { link: new GraphQLWsLink(client), client };
}
